/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import {
  actualizarOperacionFactoring,
  actualizarProveedorFactoring,
  anularOperacionFactoring,
  crearMoraFactoring,
  crearOperacionFactoringHistorica,
  eliminarOperacionFactoring,
  getOperacionesFactoring,
  getProveedoresFactoring,
  liquidarOperacionFactoring,
  vincularFacturasAOperacion,
  vincularMovimientosAOperacion,
} from "../../utils/factoring";
import { getCuentasBancarias, getMovimientosBancarios } from "../../utils/movimientosBancarios";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import LiquidarFactoringModal from "../LiquidarFactoringModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import styles from "./Factoring.module.scss";

const ESTADO_OPCIONES = [
  { value: "todos", label: "Todos" },
  { value: "activa", label: "Activas" },
  { value: "liquidada", label: "Liquidadas" },
  { value: "anulada", label: "Anuladas" },
];

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [, m, d] = f.split("-");
  return `${d}/${m}`;
};

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  if (typeof value === "string") return value;
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue, moneda) => {
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    numStr = moneda === "CLP" || moneda === "PEN" ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

// Modal inline para registrar mora sobre una operación liquidada.
const MoraModal = ({ isOpen, onClose, operacion, pais, userId, monedaPrincipal, onSuccess }) => {
  const hoy = new Date().toISOString().split("T")[0];
  const [montoMora, setMontoMora] = useState("");
  const [fechaNotificacion, setFechaNotificacion] = useState(hoy);
  const [notas, setNotas] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMontoMora("");
    setFechaNotificacion(hoy);
    setNotas("");
  }, [isOpen, hoy]);

  const handleRegistrar = async () => {
    if (!fechaNotificacion) return toast.error("Ingresá una fecha");
    const montoNum = typeof montoMora === "string" ? 0 : montoMora;
    if (!montoNum || montoNum <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setLoading(true);
    try {
      await crearMoraFactoring({
        operacion_factoring_id: operacion.id,
        pais,
        monto_mora: montoNum,
        fecha_notificacion: fechaNotificacion,
        notas: notas.trim() || null,
        created_by: userId || null,
      });
      toast.success("Mora registrada");
      onSuccess?.();
    } catch (err) {
      console.error("Error registrando mora:", err);
      toast.error("Error al registrar la mora");
    } finally {
      setLoading(false);
    }
  };

  if (!operacion) return null;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          Registrar mora — Folio {operacion.factura?.folio || "—"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Monto ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formatMontoDisplay(montoMora, pais)}
              onChange={(e) => setMontoMora(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha notificación
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaNotificacion}
              onChange={(e) => setFechaNotificacion(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            disabled={loading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleRegistrar} disabled={loading}>
          {loading ? "Registrando..." : "Registrar mora"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Modal inline para editar comisión proveedor, fecha de vencimiento,
// referencia del proveedor y giro depositado real de una operación activa.
const EditarOperacionModal = ({ isOpen, onClose, operacion, pais, monedaPrincipal, onSuccess }) => {
  const [comisionProveedor, setComisionProveedor] = useState("");
  const [fechaCobroFinal, setFechaCobroFinal] = useState("");
  const [referenciaProveedor, setReferenciaProveedor] = useState("");
  const [giroDepositado, setGiroDepositado] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !operacion) return;
    setComisionProveedor(Number(operacion.comision_proveedor || 0));
    setFechaCobroFinal(operacion.fecha_cobro_final || "");
    setReferenciaProveedor(operacion.referencia_proveedor || "");
    setGiroDepositado(operacion.giro_depositado ? Number(operacion.giro_depositado) : "");
  }, [isOpen, operacion]);

  const handleGuardar = async () => {
    const comisionNum = typeof comisionProveedor === "string" ? 0 : comisionProveedor;
    if (comisionNum < 0) {
      toast.error("Comisión inválida");
      return;
    }
    setLoading(true);
    try {
      await actualizarOperacionFactoring(operacion.id, {
        comision_proveedor: comisionNum,
        fecha_cobro_final: fechaCobroFinal || null,
        referencia_proveedor: referenciaProveedor.trim() || null,
        giro_depositado: giroDepositado !== "" ? Number(giroDepositado) : null,
      });
      toast.success("Operación actualizada");
      onSuccess?.();
    } catch (err) {
      console.error("Error actualizando operación:", err);
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  if (!operacion) return null;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          Editar operación — Folio {operacion.factura?.folio || "—"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Comisión proveedor ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formatMontoDisplay(comisionProveedor, pais)}
              onChange={(e) => setComisionProveedor(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha vencimiento
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaCobroFinal}
              onChange={(e) => setFechaCobroFinal(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Nº operación proveedor
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 406591"
              value={referenciaProveedor}
              onChange={(e) => setReferenciaProveedor(e.target.value)}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Giro depositado real ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Calculado: dejar vacío si coincide"
              value={formatMontoDisplay(giroDepositado, pais)}
              onChange={(e) => setGiroDepositado(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Modal inline para registrar una operación nueva o histórica (ya liquidada).
const NuevaOperacionModal = ({ isOpen, onClose, pais, userId, monedaPrincipal, onSuccess }) => {
  const { formatAmount } = useCurrency();
  const hoy = new Date().toISOString().split("T")[0];

  const [modo, setModo] = useState("activa"); // "activa" | "liquidada"
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [facturasSII, setFacturasSII] = useState([]);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [busquedaFactura, setBusquedaFactura] = useState("");
  const [mostrarMovimientos, setMostrarMovimientos] = useState(false);
  const [movsAdelanto, setMovsAdelanto] = useState([]);
  const [movsRemanente, setMovsRemanente] = useState([]);

  const [form, setForm] = useState({
    proveedor_factoring_id: "",
    referencia_proveedor: "",
    fecha_operacion: hoy,
    fecha_cobro_final: "",
    fecha_pago_real: "",
    pct_anticipo: "",
    tasa: "",
    tasa_mora: "",
    monto_factura: "",
    comision_proveedor: "",
    giro_depositado: "",
    cuenta_bancaria_id: "",
  });

  // Si hay facturas seleccionadas, monto es la suma — auto-calculado y read-only.
  const montoAutoCalculado =
    facturasSeleccionadas.length > 0
      ? facturasSeleccionadas.reduce((sum, f) => sum + Number(f.monto_total || 0), 0)
      : null;
  const montoF = montoAutoCalculado ?? (typeof form.monto_factura === "string" ? 0 : Number(form.monto_factura || 0));
  // parseFloat("1,85") devuelve 1 — coma corta el parsing. Normalizamos antes.
  const parsePct = (v) => parseFloat(String(v ?? "0").replace(",", ".")) / 100 || 0;
  const pctAnticipo = parsePct(form.pct_anticipo);
  const tasaF = parsePct(form.tasa);
  const tasaMoraF = parsePct(form.tasa_mora);
  const capitalAdelantado = Math.round(montoF * pctAnticipo);
  const excedentes = montoF - capitalAdelantado;
  const plazoDias = (() => {
    if (!form.fecha_cobro_final || !form.fecha_operacion) return 0;
    const d1 = new Date(form.fecha_operacion);
    const d2 = new Date(form.fecha_cobro_final);
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
  })();
  const diferenciaPrecio = Math.round(plazoDias * (tasaF / 30) * capitalAdelantado);
  const comisionProveedor = typeof form.comision_proveedor === "string" ? 0 : Number(form.comision_proveedor || 0);
  const totalCobros = diferenciaPrecio + comisionProveedor;
  const giroARecibir = capitalAdelantado - totalCobros;
  const fmt = (v) => formatAmount(v, monedaPrincipal);

  useEffect(() => {
    if (!isOpen || !pais) return;
    setForm({
      proveedor_factoring_id: "",
      referencia_proveedor: "",
      fecha_operacion: hoy,
      fecha_cobro_final: "",
      fecha_pago_real: "",
      pct_anticipo: "",
      tasa: "",
      tasa_mora: "",
      monto_factura: "",
      comision_proveedor: "",
      giro_depositado: "",
      cuenta_bancaria_id: "",
    });
    setModo("activa");
    setFacturasSeleccionadas([]);
    setBusquedaFactura("");
    setMostrarMovimientos(false);
    setMovsAdelanto([]);
    setMovsRemanente([]);
    Promise.all([
      getProveedoresFactoring(pais),
      getMovimientosBancarios(pais, { tipo: "ingreso" }),
      getCuentasBancarias(pais),
      client
        .from("facturas_sii_emitidas")
        .select("id, folio, razon_social_receptor, monto_total, fecha_emision")
        .eq("pais", pais)
        .is("estado_factoring", null)
        .order("fecha_emision", { ascending: false })
        .limit(200),
    ])
      .then(([provs, movs, ctas, facturasRes]) => {
        setProveedores(provs);
        setMovimientos(movs);
        setCuentas(ctas);
        setFacturasSII(facturasRes?.data || []);
      })
      .catch(() => toast.error("Error cargando datos"));
  }, [isOpen, pais, hoy]);

  const handleProveedorChange = (id) => {
    const prov = proveedores.find((p) => p.id === parseInt(id, 10));
    setForm((prev) => ({
      ...prev,
      proveedor_factoring_id: id,
      tasa_mora: prov?.tasa_mora ? (prov.tasa_mora * 100).toFixed(2) : "",
      pct_anticipo: prov?.pct_anticipo_habitual ? (prov.pct_anticipo_habitual * 100).toFixed(1) : "95",
    }));
  };

  const handleGuardar = async () => {
    if (!form.proveedor_factoring_id) return toast.error("Seleccioná un proveedor");
    if (!montoF || montoF <= 0) return toast.error("Ingresá el monto de la factura");
    if (!pctAnticipo) return toast.error("Ingresá el % de anticipo");
    if (!form.fecha_operacion) return toast.error("Ingresá la fecha de operación");
    if (modo === "liquidada" && !form.fecha_pago_real) return toast.error("Ingresá la fecha de pago real");

    setLoading(true);
    try {
      const payload = {
        pais,
        proveedor_factoring_id: parseInt(form.proveedor_factoring_id, 10),
        referencia_proveedor: form.referencia_proveedor.trim() || null,
        monto_factura: montoF,
        pct_anticipo: pctAnticipo,
        capital_adelantado: capitalAdelantado,
        tasa: tasaF,
        tasa_mora: tasaMoraF || null,
        plazo_dias: plazoDias || null,
        costo_financiero: diferenciaPrecio,
        comision_proveedor: comisionProveedor,
        giro_a_recibir: giroARecibir,
        fecha_operacion: form.fecha_operacion,
        fecha_cobro_final: form.fecha_cobro_final || null,
        estado: modo,
        created_by: userId || null,
        cuenta_bancaria_id: form.cuenta_bancaria_id ? parseInt(form.cuenta_bancaria_id, 10) : null,
      };

      if (form.giro_depositado && Number(form.giro_depositado) !== giroARecibir) {
        payload.giro_depositado = Number(form.giro_depositado);
      }

      if (modo === "liquidada") {
        payload.fecha_pago_real = form.fecha_pago_real;
      }

      // Compat: si hay exactamente 1 factura, también seteamos la FK directa
      // (operaciones viejas usan factura_sii_emitida_id sin tabla puente).
      // Si hay N facturas, FK directa queda null y todo va por la tabla puente.
      if (facturasSeleccionadas.length === 1) {
        payload.factura_sii_emitida_id = facturasSeleccionadas[0].id;
      }

      const nuevaOp = await crearOperacionFactoringHistorica(payload);

      // Tabla puente + UPDATE estado_factoring en cada factura vinculada.
      if (facturasSeleccionadas.length > 0) {
        await vincularFacturasAOperacion(
          nuevaOp.id,
          facturasSeleccionadas.map((f) => ({ id: f.id, monto_total: Number(f.monto_total) })),
          modo === "liquidada" ? "liquidada" : "cedida"
        );
      }

      // Tabla puente de movimientos bancarios (N por operación, separados por tipo).
      const todosMovs = [
        ...movsAdelanto.map((m) => ({ id: m.id, tipo: "adelanto" })),
        ...movsRemanente.map((m) => ({ id: m.id, tipo: "remanente" })),
      ];
      if (todosMovs.length > 0) {
        await vincularMovimientosAOperacion(nuevaOp.id, todosMovs);
      }

      toast.success(modo === "liquidada" ? "Operación histórica registrada" : "Operación registrada");
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Selector inline reutilizable de movimientos bancarios (multi-select).
  // selected: array de movs ya elegidos en este selector; excluir: movs elegidos en el otro
  // (para evitar que un mismo mov aparezca como adelanto y remanente a la vez).
  const SelectorMovimientos = ({ label, selected, onChange, excluir }) => {
    const disponibles = movimientos.filter(
      (m) => !selected.some((s) => s.id === m.id) && !excluir.some((e) => e.id === m.id)
    );
    return (
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
        {selected.map((m) => (
          <div
            key={m.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "3px 10px",
              background: "#e8f4fd",
              borderRadius: 4,
              fontSize: "0.8rem",
              marginBottom: 3,
            }}
          >
            <span style={{ color: "#1a5276" }}>
              {m.fecha ? `${m.fecha.slice(8, 10)}/${m.fecha.slice(5, 7)}` : "—"} — {m.descripcion_raw?.slice(0, 35)} —{" "}
              {fmt(Math.abs(m.monto))}
            </span>
            <button
              type="button"
              onClick={() => onChange(selected.filter((s) => s.id !== m.id))}
              style={{
                background: "none",
                border: "none",
                color: "#c0392b",
                cursor: "pointer",
                fontSize: "0.8rem",
                padding: "0 4px",
              }}
            >
              ×
            </button>
          </div>
        ))}
        <select
          className="form-control form-control-sm"
          value=""
          onChange={(e) => {
            const id = parseInt(e.target.value, 10);
            if (!id) return;
            const mov = movimientos.find((m) => m.id === id);
            if (mov) onChange([...selected, mov]);
          }}
        >
          <option value="">{selected.length === 0 ? "Sin vincular" : "+ Agregar otro movimiento"}</option>
          {disponibles.map((m) => (
            <option key={m.id} value={m.id}>
              {`${m.fecha ? `${m.fecha.slice(8, 10)}/${m.fecha.slice(5, 7)}` : "—"} — ${m.descripcion_raw?.slice(0, 40)} — ${fmt(Math.abs(m.monto))}`}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Nueva operación de factoring</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { key: "activa", label: "Operación nueva" },
            { key: "liquidada", label: "Histórica (ya liquidada)" },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModo(m.key)}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "1px solid #dee2e6",
                borderRadius: 6,
                cursor: "pointer",
                background: modo === m.key ? "#2c3e50" : "transparent",
                color: modo === m.key ? "#fff" : "#666",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Vinculación a facturas SII — múltiple */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Facturas SII emitidas (opcional)
            <span style={{ fontSize: "0.72rem", fontWeight: 400, color: "#888", marginLeft: 6 }}>
              — podés vincular una o más
            </span>
          </label>

          {facturasSeleccionadas.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {facturasSeleccionadas.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 10px",
                    background: "#e8f4fd",
                    borderRadius: 4,
                    fontSize: "0.8rem",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ color: "#27ae60", fontWeight: 600 }}>
                    ✓ Folio {f.folio} — {f.razon_social_receptor}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFacturasSeleccionadas((prev) => prev.filter((x) => x.id !== f.id))}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#c0392b",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      padding: "0 4px",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar por folio o cliente..."
            value={busquedaFactura}
            onChange={(e) => setBusquedaFactura(e.target.value)}
            style={{ marginBottom: 6 }}
          />

          {busquedaFactura.length >= 2 && (
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 4,
                maxHeight: 160,
                overflowY: "auto",
                background: "#fff",
              }}
            >
              {(() => {
                const q = busquedaFactura.toLowerCase();
                const matches = facturasSII.filter((f) => {
                  if (facturasSeleccionadas.some((x) => x.id === f.id)) return false;
                  return String(f.folio).includes(q) || (f.razon_social_receptor || "").toLowerCase().includes(q);
                });
                if (matches.length === 0) {
                  return <div style={{ padding: "10px 12px", color: "#999", fontSize: "0.82rem" }}>Sin resultados</div>;
                }
                return matches.slice(0, 20).map((f) => (
                  <div
                    key={f.id}
                    onClick={() => {
                      setFacturasSeleccionadas((prev) => [...prev, f]);
                      setBusquedaFactura("");
                    }}
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      Folio {f.folio} — {f.razon_social_receptor}
                    </span>
                    <span style={{ color: "#888", fontSize: "0.78rem" }}>{f.fecha_emision?.slice(0, 7)}</span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Proveedor de factoring
            </label>
            <select
              className="form-control form-control-sm"
              value={form.proveedor_factoring_id}
              onChange={(e) => handleProveedorChange(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Nº operación proveedor (opcional)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 406591"
              value={form.referencia_proveedor}
              onChange={(e) => setForm((p) => ({ ...p, referencia_proveedor: e.target.value }))}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: modo === "liquidada" ? "1fr 1fr 1fr" : "1fr 1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha operación
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_operacion}
              onChange={(e) => setForm((p) => ({ ...p, fecha_operacion: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha vencimiento
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_cobro_final}
              onChange={(e) => setForm((p) => ({ ...p, fecha_cobro_final: e.target.value }))}
            />
          </div>
          {modo === "liquidada" && (
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Fecha pago real *
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.fecha_pago_real}
                onChange={(e) => setForm((p) => ({ ...p, fecha_pago_real: e.target.value }))}
              />
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Monto factura
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 6.217.750"
              value={
                montoAutoCalculado != null
                  ? formatMontoDisplay(montoAutoCalculado, pais)
                  : formatMontoDisplay(form.monto_factura, pais)
              }
              onChange={(e) => {
                if (montoAutoCalculado != null) return;
                setForm((p) => ({ ...p, monto_factura: parseMontoInput(e.target.value, monedaPrincipal) }));
              }}
              readOnly={montoAutoCalculado != null}
              style={{
                background: montoAutoCalculado != null ? "#f0faf4" : undefined,
                borderColor: montoAutoCalculado != null ? "#27ae60" : undefined,
                fontWeight: montoAutoCalculado != null ? 600 : undefined,
                color: montoAutoCalculado != null ? "#198754" : undefined,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              % Anticipo
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 95"
              value={form.pct_anticipo}
              onChange={(e) => setForm((p) => ({ ...p, pct_anticipo: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Tasa mensual (%)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 2,67"
              value={form.tasa}
              onChange={(e) => setForm((p) => ({ ...p, tasa: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Tasa mora (%)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 3,79"
              value={form.tasa_mora}
              onChange={(e) => setForm((p) => ({ ...p, tasa_mora: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Comisión proveedor ($)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 48.790"
              value={formatMontoDisplay(form.comision_proveedor, pais)}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  comision_proveedor: parseMontoInput(e.target.value, monedaPrincipal),
                }))
              }
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Cuenta bancaria destino
            </label>
            <select
              className="form-control form-control-sm"
              value={form.cuenta_bancaria_id}
              onChange={(e) => setForm((p) => ({ ...p, cuenta_bancaria_id: e.target.value }))}
            >
              <option value="">Seleccionar...</option>
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>
                  {`${c.bancos?.nombre || "Banco"} ${c.moneda} — ${c.numero_cuenta}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {montoF > 0 && pctAnticipo > 0 && (
          <div
            style={{
              background: "#fff3cd",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 12,
              fontSize: "0.82rem",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", color: "#666" }}>
              <span>Capital adelantado ({form.pct_anticipo}%):</span>
              <span>{fmt(capitalAdelantado)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#666", marginTop: 4 }}>
              <span>Plazo:</span>
              <span>{plazoDias} días</span>
            </div>
            <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Diferencia precio:</span>
              <span style={{ color: "#c0392b" }}>{fmt(diferenciaPrecio)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span>Comisión:</span>
              <span style={{ color: "#c0392b" }}>{fmt(comisionProveedor)}</span>
            </div>
            <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "0.95rem" }}>
              <span>GIRO A RECIBIR:</span>
              <span style={{ color: "#2e7d32" }}>{fmt(giroARecibir)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span>Excedentes:</span>
              <span style={{ color: "#2980b9" }}>{fmt(excedentes)}</span>
            </div>
          </div>
        )}

        {modo === "liquidada" && (
          <div style={{ marginTop: 12 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "#555",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={mostrarMovimientos}
                onChange={(e) => setMostrarMovimientos(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              Vincular movimientos bancarios
            </label>

            {mostrarMovimientos && (
              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "12px 14px",
                  background: "#f8f9fa",
                }}
              >
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Giro depositado real ($) — si difiere del calculado
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={`Calculado: ${fmt(giroARecibir)} — dejá vacío si coincide`}
                    value={formatMontoDisplay(form.giro_depositado, pais)}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        giro_depositado: parseMontoInput(e.target.value, monedaPrincipal),
                      }))
                    }
                  />
                </div>

                <SelectorMovimientos
                  label="Movimientos bancarios — Giro (adelanto)"
                  selected={movsAdelanto}
                  onChange={setMovsAdelanto}
                  excluir={movsRemanente}
                />
                <SelectorMovimientos
                  label="Movimientos bancarios — Excedente (remanente)"
                  selected={movsRemanente}
                  onChange={setMovsRemanente}
                  excluir={movsAdelanto}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Guardando..." : modo === "liquidada" ? "Registrar histórica" : "Registrar operación"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Factoring = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const monedaPrincipal = pais === "Chile" ? "CLP" : "PEN";

  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [liquidarModal, setLiquidarModal] = useState(null);
  const [moraModal, setMoraModal] = useState(null);
  const [editarModal, setEditarModal] = useState(null);
  const [anulandoId, setAnulandoId] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [nuevaOpModal, setNuevaOpModal] = useState(false);
  const [vincularModal, setVincularModal] = useState(null);
  const [proveedoresPanel, setProveedoresPanel] = useState([]);
  const [proveedoresPanelOpen, setProveedoresPanelOpen] = useState(false);
  const [editandoProveedor, setEditandoProveedor] = useState(null);
  const [savingProveedor, setSavingProveedor] = useState(false);

  const cargar = async () => {
    if (!pais) return;
    setLoading(true);
    try {
      const data = await getOperacionesFactoring(pais);
      setOperaciones(data);
    } catch (err) {
      console.error("Error cargando factoring:", err);
      toast.error("Error cargando operaciones de factoring");
    } finally {
      setLoading(false);
    }
  };

  const cargarProveedores = async () => {
    if (!pais) return;
    try {
      const data = await getProveedoresFactoring(pais);
      setProveedoresPanel(data);
    } catch {
      toast.error("Error cargando proveedores");
    }
  };

  const handleGuardarProveedor = async () => {
    if (!editandoProveedor) return;
    setSavingProveedor(true);
    try {
      await actualizarProveedorFactoring(editandoProveedor.id, {
        tasa_mora: editandoProveedor.tasa_mora !== "" ? parseFloat(editandoProveedor.tasa_mora) / 100 : null,
        pct_anticipo_habitual:
          editandoProveedor.pct_anticipo_habitual !== ""
            ? parseFloat(editandoProveedor.pct_anticipo_habitual) / 100
            : null,
      });
      toast.success("Proveedor actualizado");
      setEditandoProveedor(null);
      cargarProveedores();
    } catch (err) {
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setSavingProveedor(false);
    }
  };

  useEffect(() => {
    cargar();
    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  // Operaciones con mora_pendiente derivada de la subcolección embed.
  const operacionesConMora = useMemo(
    () =>
      operaciones.map((op) => ({
        ...op,
        mora_pendiente: (op.moras || [])
          .filter((m) => m.estado === "pendiente")
          .reduce((s, m) => s + Number(m.monto_mora || 0), 0),
      })),
    [operaciones]
  );

  const operacionesFiltradas = useMemo(
    () => (filtroEstado === "todos" ? operacionesConMora : operacionesConMora.filter((o) => o.estado === filtroEstado)),
    [operacionesConMora, filtroEstado]
  );

  const resumen = useMemo(() => {
    const totalCedido = operaciones.reduce((s, o) => s + Number(o.monto_factura || 0), 0);
    // Costo real = diferencia precio (costo_financiero) + comisión fija del proveedor.
    // El IVA del financiamiento es impuesto, no costo: se excluye del total.
    const totalCosto = operaciones.reduce(
      (s, o) => s + Number(o.costo_financiero || 0) + Number(o.comision_proveedor || 0),
      0
    );
    const activas = operaciones.filter((o) => o.estado === "activa").length;
    const liquidadas = operaciones.filter((o) => o.estado === "liquidada").length;
    const anuladas = operaciones.filter((o) => o.estado === "anulada").length;
    return { totalCedido, totalCosto, activas, liquidadas, anuladas };
  }, [operaciones]);

  const handleAnular = async (op) => {
    const mensaje =
      `¿Anular operación de factoring para folio ${op.factura?.folio}?\n` +
      "La factura volverá a aparecer en «Por cobrar».";
    if (!window.confirm(mensaje)) return;
    setAnulandoId(op.id);
    try {
      await anularOperacionFactoring(op.id, op.factura_sii_emitida_id);
      toast.success("Operación anulada — factura liberada");
      await cargar();
    } catch (err) {
      console.error("Error anulando:", err);
      toast.error("Error al anular");
    } finally {
      setAnulandoId(null);
    }
  };

  const handleEliminar = async (op) => {
    const mensaje =
      `¿Eliminar definitivamente la operación anulada del Folio ${op.factura?.folio}?\n` +
      "Esta acción no se puede deshacer.";
    if (!window.confirm(mensaje)) return;
    setEliminandoId(op.id);
    try {
      await eliminarOperacionFactoring(op.id);
      toast.success("Operación eliminada");
      await cargar();
    } catch (err) {
      console.error("Error eliminando:", err);
      toast.error("Error al eliminar");
    } finally {
      setEliminandoId(null);
    }
  };

  // Mantengo el handler de liquidar viejo solo para fallback; el flujo principal pasa
  // por LiquidarFactoringModal. Preserva el setState optimista para no recargar todo.
  // eslint-disable-next-line no-unused-vars
  const handleLiquidarLegacy = async (op, fechaPagoReal) => {
    try {
      await liquidarOperacionFactoring(op, { pais, fechaPagoReal, monedaPrincipal });
      toast.success("Operación liquidada");
      await cargar();
    } catch (err) {
      console.error("Error liquidando:", err);
      toast.error("Error al liquidar la operación");
    }
  };

  const renderEstadoBadge = (estado) => {
    if (estado === "activa") {
      return <span className={`${styles.estadoBadge} ${styles.estadoActiva}`}>Activa</span>;
    }
    if (estado === "liquidada") {
      return <span className={`${styles.estadoBadge} ${styles.estadoLiquidada}`}>Liquidada</span>;
    }
    return (
      <span className={styles.estadoBadge} style={{ background: "#e0e0e0", color: "#555" }}>
        Anulada
      </span>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Header>
        <button type="button" className={styles.btnNuevaOp} onClick={() => setNuevaOpModal(true)}>
          + Nueva operación
        </button>
      </Header>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div className={styles.cardsSection}>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Total cedido</div>
                <div className={styles.cardValue}>{formatAmount(resumen.totalCedido, monedaPrincipal)}</div>
              </div>
              <div className={`${styles.card} ${styles.cardRed}`}>
                <div className={styles.cardLabel}>Costo acumulado</div>
                <div className={styles.cardValue}>{formatAmount(resumen.totalCosto, monedaPrincipal)}</div>
              </div>
              <div className={`${styles.card} ${styles.cardOrange}`}>
                <div className={styles.cardLabel}>Operaciones activas</div>
                <div className={styles.cardValue}>{resumen.activas}</div>
              </div>
              <div className={`${styles.card} ${styles.cardGreen}`}>
                <div className={styles.cardLabel}>Liquidadas</div>
                <div className={styles.cardValue}>{resumen.liquidadas}</div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Anuladas</div>
                <div className={styles.cardValue}>{resumen.anuladas}</div>
              </div>
            </div>

            <div className={styles.filtros}>
              <label htmlFor="filtroEstado" style={{ color: "#666", margin: 0 }}>
                Estado:
              </label>
              <select
                id="filtroEstado"
                className={`form-select form-select-sm ${styles.filtroSelect}`}
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
              >
                {ESTADO_OPCIONES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span style={{ color: "#999", fontSize: "0.78rem", marginLeft: "auto" }}>
                {operacionesFiltradas.length} de {operaciones.length}
              </span>
            </div>

            {operacionesFiltradas.length === 0 ? (
              <div className={styles.empty}>Sin operaciones de factoring registradas.</div>
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.tableResponsive}>
                  <table className={`table table-hover table-striped ${styles.table}`}>
                    <thead>
                      <tr>
                        <th className={styles.colFecha}>Fecha</th>
                        <th className={styles.colFolio}>Folio</th>
                        <th className={styles.colRef}>Ref.</th>
                        <th className={styles.colCliente}>Cliente</th>
                        <th className={styles.colProveedor}>Proveedor</th>
                        <th className={styles.colMonto}>Monto</th>
                        <th className={styles.colPct}>Anticipo</th>
                        <th className={styles.colTasa}>Tasa</th>
                        <th className={styles.colPlazo}>Plazo</th>
                        <th className={styles.colGiro}>Giro recibido</th>
                        <th className={styles.colExcedente}>Excedentes</th>
                        <th className={styles.colVenc}>Vencimiento</th>
                        <th className={styles.colEstado}>Estado</th>
                        <th className={styles.colAccion}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operacionesFiltradas.map((op) => {
                        const vinc = op.facturas_vinculadas || [];
                        const foliosArr = vinc.map((fv) => fv.factura?.folio).filter(Boolean);
                        const foliosTxt = foliosArr.length > 0 ? foliosArr.join(" + ") : op.factura?.folio || "";
                        const nombresArr = [
                          ...new Set(vinc.map((fv) => fv.factura?.razon_social_receptor).filter(Boolean)),
                        ];
                        const nombresTxt =
                          nombresArr.length > 0 ? nombresArr.join(" / ") : op.factura?.razon_social_receptor || "";
                        const tieneAdelantoVinc =
                          !!op.movimiento_bancario_adelanto_id ||
                          (op.movimientos_vinculados || []).some((mv) => mv.tipo === "adelanto");
                        return (
                          <tr key={op.id}>
                            <td className={styles.colFecha}>{formatFecha(op.fecha_operacion)}</td>
                            <td className={styles.colFolio}>{foliosTxt || "—"}</td>
                            <td className={styles.colRef} title={op.referencia_proveedor || ""}>
                              {op.referencia_proveedor || "—"}
                            </td>
                            <td className={styles.colCliente} title={nombresTxt}>
                              {nombresTxt || "—"}
                            </td>
                            <td className={styles.colProveedor}>{op.proveedor?.nombre || "—"}</td>
                            <td className={styles.colMonto}>{formatAmount(op.monto_factura, monedaPrincipal)}</td>
                            <td className={styles.colPct}>
                              {op.pct_anticipo ? `${(op.pct_anticipo * 100).toFixed(1)}%` : "—"}
                            </td>
                            <td className={styles.colTasa}>{op.tasa ? `${(op.tasa * 100).toFixed(2)}%` : "—"}</td>
                            <td className={styles.colPlazo}>{op.plazo_dias || "—"}</td>
                            <td className={styles.colGiro}>
                              <span style={{ color: "#198754", fontWeight: 600 }}>
                                {op.giro_depositado || op.giro_a_recibir
                                  ? formatAmount(op.giro_depositado || op.giro_a_recibir, monedaPrincipal)
                                  : "—"}
                              </span>
                              {op.giro_depositado && (
                                <span className={styles.tooltipWrapper}>
                                  <span className={styles.tooltipDot}>●</span>
                                  <span className={styles.tooltipBox}>
                                    {`Depositado: ${formatAmount(Number(op.giro_depositado), monedaPrincipal)}`}
                                    <br />
                                    {`Calculado: ${formatAmount(Number(op.giro_a_recibir), monedaPrincipal)}`}
                                    <br />
                                    {`Diferencia: ${formatAmount(
                                      Number(op.giro_depositado) - Number(op.giro_a_recibir),
                                      monedaPrincipal
                                    )}`}
                                  </span>
                                </span>
                              )}
                            </td>
                            <td className={styles.colExcedente} style={{ color: "#1d4e89" }}>
                              {formatAmount(
                                op.monto_remanente ?? Number(op.monto_factura) - Number(op.capital_adelantado),
                                monedaPrincipal
                              )}
                            </td>
                            <td className={styles.colVenc}>{formatFecha(op.fecha_cobro_final)}</td>
                            <td className={styles.colEstado}>{renderEstadoBadge(op.estado)}</td>
                            <td className={styles.colAccion} onClick={(e) => e.stopPropagation()}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 4,
                                  justifyContent: "center",
                                  flexWrap: "nowrap",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {op.estado === "activa" && (
                                  <>
                                    <button
                                      type="button"
                                      className={styles.btnLiquidar}
                                      onClick={() => setLiquidarModal(op)}
                                    >
                                      Liquidar
                                    </button>
                                    {!tieneAdelantoVinc && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setVincularModal({
                                            id: null,
                                            tipo: "ingreso",
                                            monto: op.giro_a_recibir || 0,
                                            moneda: monedaPrincipal,
                                            fecha: new Date().toISOString().split("T")[0],
                                            descripcion_raw: `Factoring — ${op.factura?.razon_social_receptor || ""} Folio ${op.factura?.folio || ""}`,
                                            pais,
                                            _factoringOpId: op.id,
                                            _factoringTipo: "giro",
                                          })
                                        }
                                        style={{
                                          background: "none",
                                          border: "1px solid #8e44ad",
                                          borderRadius: 4,
                                          padding: "3px 8px",
                                          fontSize: "0.75rem",
                                          color: "#8e44ad",
                                          cursor: "pointer",
                                        }}
                                      >
                                        Vincular
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setEditarModal(op)}
                                      style={{
                                        background: "none",
                                        border: "1px solid #0d6efd",
                                        borderRadius: 4,
                                        padding: "3px 8px",
                                        fontSize: "0.75rem",
                                        color: "#0d6efd",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleAnular(op)}
                                      disabled={anulandoId === op.id}
                                      style={{
                                        background: "none",
                                        border: "1px solid #c62828",
                                        borderRadius: 4,
                                        padding: "3px 8px",
                                        fontSize: "0.75rem",
                                        color: "#c62828",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {anulandoId === op.id ? "..." : "Anular"}
                                    </button>
                                  </>
                                )}
                                {op.estado === "liquidada" && (
                                  <button
                                    type="button"
                                    onClick={() => setMoraModal(op)}
                                    style={{
                                      background: "none",
                                      border: "1px solid #e67e22",
                                      borderRadius: 4,
                                      padding: "3px 8px",
                                      fontSize: "0.75rem",
                                      color: "#e67e22",
                                      cursor: "pointer",
                                    }}
                                  >
                                    + Mora
                                  </button>
                                )}
                                {op.estado === "anulada" && (
                                  <button
                                    type="button"
                                    onClick={() => handleEliminar(op)}
                                    disabled={eliminandoId === op.id}
                                    style={{
                                      background: "none",
                                      border: "1px solid #c62828",
                                      borderRadius: 4,
                                      padding: "3px 10px",
                                      fontSize: "0.75rem",
                                      color: "#c62828",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {eliminandoId === op.id ? "..." : "Eliminar"}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Panel proveedores de factoring */}
            <div style={{ marginTop: 24 }}>
              <button
                type="button"
                onClick={() => setProveedoresPanelOpen((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#2c3e50",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  padding: "4px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{proveedoresPanelOpen ? "▾" : "▸"}</span>
                Proveedores de factoring
              </button>

              {proveedoresPanelOpen && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.83rem" }}>
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "30%" }}>Proveedor</th>
                        <th style={{ width: "17%", textAlign: "center" }}>Tasa mora (%)</th>
                        <th style={{ width: "18%", textAlign: "center" }}>% Anticipo habitual</th>
                        <th style={{ width: "15%", textAlign: "center" }}>Tipo</th>
                        <th style={{ width: "20%", textAlign: "center" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proveedoresPanel.map((prov) => {
                        const esEditando = editandoProveedor?.id === prov.id;
                        return (
                          <tr key={prov.id}>
                            <td style={{ fontWeight: 600 }}>{prov.nombre}</td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 70, margin: "0 auto", textAlign: "center" }}
                                  value={editandoProveedor.tasa_mora}
                                  onChange={(e) => setEditandoProveedor((p) => ({ ...p, tasa_mora: e.target.value }))}
                                  placeholder="ej: 3.27"
                                />
                              ) : prov.tasa_mora ? (
                                `${(prov.tasa_mora * 100).toFixed(2)}%`
                              ) : (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 70, margin: "0 auto", textAlign: "center" }}
                                  value={editandoProveedor.pct_anticipo_habitual}
                                  onChange={(e) =>
                                    setEditandoProveedor((p) => ({ ...p, pct_anticipo_habitual: e.target.value }))
                                  }
                                  placeholder="ej: 95"
                                />
                              ) : prov.pct_anticipo_habitual ? (
                                `${(prov.pct_anticipo_habitual * 100).toFixed(1)}%`
                              ) : (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center", color: "#888" }}>{prov.tipo || "—"}</td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                  <button
                                    type="button"
                                    onClick={handleGuardarProveedor}
                                    disabled={savingProveedor}
                                    style={{
                                      background: "#198754",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 4,
                                      padding: "3px 10px",
                                      fontSize: "0.75rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {savingProveedor ? "..." : "Guardar"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditandoProveedor(null)}
                                    style={{
                                      background: "none",
                                      border: "1px solid #999",
                                      borderRadius: 4,
                                      padding: "3px 8px",
                                      fontSize: "0.75rem",
                                      cursor: "pointer",
                                      color: "#666",
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditandoProveedor({
                                      ...prov,
                                      tasa_mora: prov.tasa_mora ? (prov.tasa_mora * 100).toFixed(2) : "",
                                      pct_anticipo_habitual: prov.pct_anticipo_habitual
                                        ? (prov.pct_anticipo_habitual * 100).toFixed(1)
                                        : "",
                                    })
                                  }
                                  style={{
                                    background: "none",
                                    border: "1px solid #0d6efd",
                                    borderRadius: 4,
                                    padding: "3px 10px",
                                    fontSize: "0.75rem",
                                    color: "#0d6efd",
                                    cursor: "pointer",
                                  }}
                                >
                                  Editar
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {liquidarModal && (
        <LiquidarFactoringModal
          isOpen={!!liquidarModal}
          onClose={() => setLiquidarModal(null)}
          operacion={liquidarModal}
          pais={pais}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setLiquidarModal(null);
            cargar();
          }}
        />
      )}

      {moraModal && (
        <MoraModal
          isOpen={!!moraModal}
          onClose={() => setMoraModal(null)}
          operacion={moraModal}
          pais={pais}
          userId={userId}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setMoraModal(null);
            cargar();
          }}
        />
      )}

      {editarModal && (
        <EditarOperacionModal
          isOpen={!!editarModal}
          onClose={() => setEditarModal(null)}
          operacion={editarModal}
          pais={pais}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setEditarModal(null);
            cargar();
          }}
        />
      )}

      {nuevaOpModal && (
        <NuevaOperacionModal
          isOpen={nuevaOpModal}
          onClose={() => setNuevaOpModal(false)}
          pais={pais}
          userId={userId}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setNuevaOpModal(false);
            cargar();
          }}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={() => {
            setVincularModal(null);
            cargar();
          }}
        />
      )}
    </div>
  );
};

export default Factoring;
